// src/views/admin/ADTrimestres.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box">
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const EMPTY_TRIM = { nom: "", annee_scolaire: "", date_debut: "", date_fin: "" };
const EMPTY_SOUS = { nom: "", trimestre_physique: "", date_debut: "", date_fin: "" };

export default function ADTrimestres({ slug }) {
  const [activeTab, setActiveTab] = useState("trimestres");

  const [annees, setAnnees] = useState([]);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [trimestres, setTrimestres] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [formTrim, setFormTrim] = useState(EMPTY_TRIM);
  const [formSous, setFormSous] = useState(EMPTY_SOUS);

  const toast = useToastHook();
  const {
    getAnneesScolaires,
    getTrimestres, createTrimestre, updateTrimestre, deleteTrimestre,
    createSousTrimestre, updateSousTrimestre, deleteSousTrimestre,
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
  }, [slug]);

  useEffect(() => {
    if (slug && anneeFilt) loadTrimestres();
  }, [slug, anneeFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const anneesData = await getAnneesScolaires(slug, { show_archived: "false" });
      setAnnees(anneesData);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les années scolaires");
    } finally {
      setLoading(false);
    }
  };

  const loadTrimestres = async () => {
    try {
      const data = await getTrimestres(slug, { annee_scolaire: anneeFilt });
      setTrimestres(data);
    } catch (error) {
      toast.error("Impossible de charger les trimestres");
    }
  };

  const sousTrimestres = trimestres.flatMap(t =>
    (t.sous_trimestres || []).map(st => ({ ...st, trimestre_nom: t.nom }))
  );

  const filteredTrimestres = trimestres.filter(t => t.nom?.toLowerCase().includes(search.toLowerCase()));
  const filteredSous = sousTrimestres.filter(st =>
    st.nom?.toLowerCase().includes(search.toLowerCase()) ||
    st.trimestre_nom?.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("fr-FR"); } catch { return d; }
  };

  // ── Trimestres ───────────────────────────────────────────────────────
  const openCreateTrim = () => { setFormTrim({ ...EMPTY_TRIM, annee_scolaire: anneeFilt }); setModal("create-trim"); };
  const openEditTrim = (t) => {
    setSelected(t);
    setFormTrim({ nom: t.nom, annee_scolaire: t.annee_scolaire, date_debut: t.date_debut || "", date_fin: t.date_fin || "" });
    setModal("edit-trim");
  };
  const openDeleteTrim = (t) => { setSelected(t); setModal("delete-trim"); };

  const handleSaveTrim = async () => {
    if (!formTrim.nom.trim()) { toast.error("Le nom est requis"); return; }
    setSaving(true);
    try {
      const dataToSend = {
        nom: formTrim.nom.trim(),
        annee_scolaire: formTrim.annee_scolaire || anneeFilt,
        date_debut: formTrim.date_debut || null,
        date_fin: formTrim.date_fin || null,
      };
      if (modal === "create-trim") {
        const created = await createTrimestre(slug, dataToSend);
        setTrimestres(prev => [...prev, created]);
        toast.success(`Trimestre "${created.nom}" créé avec succès`);
      } else {
        const updated = await updateTrimestre(slug, selected.id, dataToSend);
        setTrimestres(prev => prev.map(t => t.id === selected.id ? updated : t));
        toast.success(`Trimestre "${updated.nom}" modifié avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.nom?.[0] || data?.annee_scolaire || "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrim = async () => {
    setSaving(true);
    try {
      await deleteTrimestre(slug, selected.id);
      setTrimestres(prev => prev.filter(t => t.id !== selected.id));
      toast.success(`Trimestre "${selected.nom}" supprimé avec succès`);
      setModal(null);
    } catch (error) {
      toast.error("Impossible de supprimer ce trimestre");
    } finally {
      setSaving(false);
    }
  };

  // ── Sous-trimestres ──────────────────────────────────────────────────
  const openCreateSous = () => {
    setFormSous({ ...EMPTY_SOUS, trimestre_physique: trimestres[0]?.id || "" });
    setModal("create-sous");
  };
  const openEditSous = (st) => {
    setSelected(st);
    setFormSous({
      nom: st.nom,
      trimestre_physique: st.trimestre_physique,
      date_debut: st.date_debut || "",
      date_fin: st.date_fin || "",
    });
    setModal("edit-sous");
  };
  const openDeleteSous = (st) => { setSelected(st); setModal("delete-sous"); };

  const handleSaveSous = async () => {
    if (!formSous.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!formSous.trimestre_physique) { toast.error("Le trimestre est requis"); return; }
    setSaving(true);
    try {
      const dataToSend = {
        nom: formSous.nom.trim(),
        trimestre_physique: formSous.trimestre_physique,
        date_debut: formSous.date_debut || null,
        date_fin: formSous.date_fin || null,
      };
      if (modal === "create-sous") {
        await createSousTrimestre(slug, dataToSend);
        toast.success(`Sous-trimestre "${formSous.nom}" créé avec succès`);
      } else {
        await updateSousTrimestre(slug, selected.id, dataToSend);
        toast.success(`Sous-trimestre "${formSous.nom}" modifié avec succès`);
      }
      setModal(null);
      await loadTrimestres();
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === "object" ? Object.values(data).flat().join("\n") : "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSous = async () => {
    setSaving(true);
    try {
      await deleteSousTrimestre(slug, selected.id);
      toast.success(`Sous-trimestre "${selected.nom}" supprimé avec succès`);
      setModal(null);
      await loadTrimestres();
    } catch (error) {
      toast.error("Impossible de supprimer ce sous-trimestre");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {/* Onglets comme dans Configuration */}
      <div className="tab-nav">
        <button
          className={`tab-btn ${activeTab === "trimestres" ? "active" : ""}`}
          onClick={() => setActiveTab("trimestres")}
        >
          <i className="ti ti-calendar-time" /> TRIMESTRES
        </button>
        <button
          className={`tab-btn ${activeTab === "sous-trimestres" ? "active" : ""}`}
          onClick={() => setActiveTab("sous-trimestres")}
        >
          <i className="ti ti-calendar-stats" /> SOUS-TRIMESTRES
        </button>
      </div>


      {/* Barre d'outils */}
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <input type="text" placeholder="Rechercher…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }} />
        <button className="bsec" onClick={loadTrimestres}><i className="ti ti-refresh" />Actualiser</button>
        {activeTab === "trimestres"
          ? <button className="bprim" onClick={openCreateTrim}><i className="ti ti-plus" />Nouveau trimestre</button>
          : <button className="bprim" onClick={openCreateSous} disabled={trimestres.length === 0}><i className="ti ti-plus" />Nouveau sous-trimestre</button>}
      </div>

      {/* ─── TAB TRIMESTRES ─── */}
      {activeTab === "trimestres" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Trimestre</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Sous-trimestres</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrimestres.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>{t.nom}</td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>{formatDate(t.date_debut)}</td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>{formatDate(t.date_fin)}</td>
                  <td>
                    <span className="pill neu">{(t.sous_trimestres || []).length} sous-période(s)</span>
                  </td>
                  <td>
                    <div className="act">
                      {/* Modifier - Jaune */}
                      <button 
                        title="Modifier" 
                        onClick={() => openEditTrim(t)}
                        style={{ background: "#ffc107", color: "black" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      {/* Supprimer - Rouge */}
                      <button 
                        className="del" 
                        title="Supprimer" 
                        onClick={() => openDeleteTrim(t)}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTrimestres.length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun trimestre trouvé</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── TAB SOUS-TRIMESTRES ─── */}
      {activeTab === "sous-trimestres" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Sous-trimestre</th>
                <th>Trimestre</th>
                <th>Début</th>
                <th>Fin</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSous.map(st => (
                <tr key={st.id}>
                  <td style={{ fontWeight: 500 }}>{st.nom}</td>
                  <td>{st.trimestre_nom}</td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>{formatDate(st.date_debut)}</td>
                  <td style={{ fontSize: 13, color: "var(--m)" }}>{formatDate(st.date_fin)}</td>
                  <td>
                    <div className="act">
                      {/* Modifier - Jaune */}
                      <button 
                        title="Modifier" 
                        onClick={() => openEditSous(st)}
                        style={{ background: "#ffc107", color: "black" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-edit" />
                      </button>
                      {/* Supprimer - Rouge */}
                      <button 
                        className="del" 
                        title="Supprimer" 
                        onClick={() => openDeleteSous(st)}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSous.length === 0 && (
                <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
                  {trimestres.length === 0 ? "Créez d'abord un trimestre." : "Aucun sous-trimestre trouvé"}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ─── MODALS ─── */}

      {/* Modal Trimestre */}
      {(modal === "create-trim" || modal === "edit-trim") && (
        <Modal title={modal === "create-trim" ? "Nouveau trimestre" : "Modifier le trimestre"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg"><label>Nom *</label><input value={formTrim.nom} onChange={e => setFormTrim(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: 1er trimestre" /></div>
            <div className="fgrid">
              <div className="fg"><label>Date début</label><input type="date" value={formTrim.date_debut} onChange={e => setFormTrim(p => ({ ...p, date_debut: e.target.value }))} /></div>
              <div className="fg"><label>Date fin</label><input type="date" value={formTrim.date_fin} onChange={e => setFormTrim(p => ({ ...p, date_fin: e.target.value }))} /></div>
            </div>
            <div className="fg">
              <label>Année scolaire</label>
              <select value={formTrim.annee_scolaire} onChange={e => setFormTrim(p => ({ ...p, annee_scolaire: e.target.value }))}>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveTrim} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create-trim" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer Trimestre */}
      {modal === "delete-trim" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer <strong>{selected.nom}</strong> ?
            <div style={{ color: "var(--rd)", marginTop: 8 }}>
              ⚠️ Les sous-trimestres, plannifications, notes et bulletins liés seront supprimés aussi.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteTrim} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Sous-trimestre */}
      {(modal === "create-sous" || modal === "edit-sous") && (
        <Modal title={modal === "create-sous" ? "Nouveau sous-trimestre" : "Modifier le sous-trimestre"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg"><label>Nom *</label><input value={formSous.nom} onChange={e => setFormSous(p => ({ ...p, nom: e.target.value }))} placeholder="Ex: 1ère période" /></div>
            <div className="fg">
              <label>Trimestre *</label>
              <select value={formSous.trimestre_physique} onChange={e => setFormSous(p => ({ ...p, trimestre_physique: e.target.value }))}>
                <option value="">-- Sélectionner --</option>
                {trimestres.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </div>
            <div className="fgrid">
              <div className="fg"><label>Date début</label><input type="date" value={formSous.date_debut} onChange={e => setFormSous(p => ({ ...p, date_debut: e.target.value }))} /></div>
              <div className="fg"><label>Date fin</label><input type="date" value={formSous.date_fin} onChange={e => setFormSous(p => ({ ...p, date_fin: e.target.value }))} /></div>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveSous} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create-sous" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal Supprimer Sous-trimestre */}
      {modal === "delete-sous" && selected && (
        <Modal title="Confirmer la suppression" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>
            Supprimer <strong>{selected.nom}</strong> ({selected.trimestre_nom}) ?
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDeleteSous} disabled={saving}>
              {saving ? "..." : <><i className="ti ti-trash" />Supprimer</>}
            </button>
          </div>
        </Modal>
      )}

      <style>{`
/* ─── ONGLETS (thème bleu + or) ─── */
.tab-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  background: linear-gradient(180deg, var(--pr-dk) 0%, var(--pr) 100%);
  border-radius: 10px;
  border-bottom: 3px solid var(--or);
  padding: 8px 12px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: transparent;
  color: rgba(255,255,255,0.75);
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.tab-btn i { font-size: 18px; }

.tab-btn:hover {
  background: rgba(255,255,255,0.12);
  color: var(--or-lt);
}

.tab-btn.active {
  background: white;
  color: var(--pr);
  box-shadow: inset 0 -3px 0 var(--or), 0 2px 8px rgba(0,0,0,0.15);
}
.tab-btn.active i { color: var(--or); }
.tab-btn.active:hover { background: white; color: var(--pr); }

        .fgrid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 9px;
        }

        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }

        .act button:hover {
          opacity: 0.8 !important;
        }

        .act button.del {
          background: var(--rd);
          color: white;
        }

        .act button.del:hover {
          background: var(--rd-dk);
        }

        @media (max-width: 768px) {
          .tab-btn { padding: 6px 10px; font-size: 11px; }
          .tab-btn i { font-size: 14px; }
          .fgrid { grid-template-columns: 1fr; }
        }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
          .tab-btn i { font-size: 18px; margin: 0; }
        }
      `}</style>
    </div>
  );
}